import { useEffect, useRef, useSyncExternalStore } from 'react'
import useUserLocation from './useUserLocation'
import { getNfcSelection, getNfcReaderPort } from '@/lib/nfc'

// Shared NFC status across components:
// { ws: bool, reader: bool (selected reader detected), readers: [labels] }
const defaultStatus = { ws: false, reader: false, readers: [] }

function setNfcStatus(update) {
    window.__nfcStatus = { ...(window.__nfcStatus ?? defaultStatus), ...update }
    window.dispatchEvent(new Event('nfc-status'))
}

function subscribe(cb) {
    window.addEventListener('nfc-status', cb)
    return () => window.removeEventListener('nfc-status', cb)
}

function getSnapshot() {
    return window.__nfcStatus ?? defaultStatus
}

export function useNfcStatus() {
    return useSyncExternalStore(subscribe, getSnapshot)
}

export default function useNfcScanner(onScan) {
    const { nfcReader } = useUserLocation()
    const onScanRef = useRef(onScan)
    onScanRef.current = onScan

    useEffect(() => {
        setNfcStatus(defaultStatus)
        const selection = getNfcSelection(nfcReader)
        if (!selection) return
        const { server, port } = selection
        let disposed = false
        let socket = null
        let reconnectTimer
        let connectionTimer
        let urlIndex = 0
        let retryDelay = 3000

        function retry() {
            if (disposed) return
            urlIndex = (urlIndex + 1) % server.urls.length
            reconnectTimer = setTimeout(connect, retryDelay)
            retryDelay = Math.min(retryDelay * 2, 60000)
        }

        function connect() {
            if (disposed) return
            let ws
            try {
                ws = new WebSocket(server.urls[urlIndex])
            } catch {
                retry()
                return
            }
            socket = ws
            connectionTimer = setTimeout(() => ws.close(), 10000)

            ws.onopen = () => {
                if (disposed) return
                clearTimeout(connectionTimer)
                retryDelay = 3000
                setNfcStatus({ ws: true })
            }

            ws.onmessage = (event) => {
                if (disposed) return
                let data
                try {
                    data = JSON.parse(event.data)
                } catch {
                    return
                }
                if (!data || typeof data !== 'object') return
                // Old firmware without server identity is accepted only on the original Pi.
                if (data.server?.id ? data.server.id !== server.id : server.id !== 'printserver') {
                    setNfcStatus(defaultStatus)
                    ws.close()
                    return
                }
                if (data.type === 'reader_status') {
                    const readers = Array.isArray(data.readers) ? data.readers : []
                    setNfcStatus({
                        reader: readers.some((reader) => getNfcReaderPort(reader, server.id) === port),
                        readers,
                    })
                } else if (data.type === 'card_read' && data.records?.[0]) {
                    if (getNfcReaderPort(data.reader, server.id) !== port) return
                    onScanRef.current?.(data.records[0].value)
                }
            }

            ws.onclose = () => {
                clearTimeout(connectionTimer)
                if (disposed) return
                socket = null
                setNfcStatus(defaultStatus)
                retry()
            }
            ws.onerror = () => ws.close()
        }

        connect()
        return () => {
            disposed = true
            clearTimeout(reconnectTimer)
            clearTimeout(connectionTimer)
            socket?.close()
        }
    }, [nfcReader])
}
